/**
 * DATENSCHUTZPAGE.JS v3 - WEISSES DESIGN + PLANIVIO TEAL
 * 
 * ✅ v3: Weißes Design + Planivio Teal/Green
 *        - Weißer Hintergrund
 *        - Teal/Green Farbschema
 *        - Echtes Logo
 * ✅ v2: i18n Support
 * ✅ v1: DSGVO-konforme Datenschutzerklärung
 * 
 * @version 3.0.0 - Weißes Design + Planivio Teal
 */

import React, { useState, useEffect } from 'react';
import { useTranslation, LANGUAGES } from '../context/I18nContext';
import { hasAnalyticsConsent, revokeAnalyticsConsent, acceptAnalytics, getConsentInfo } from '../utils/cookieConsent';
import { usePublicPageView } from '../hooks/useAnalytics';

// ============================================================================
// PLANIVIO FARBEN
// ============================================================================
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)'
};

const COLORS = {
  bgWhite: '#FFFFFF',
  bgLight: '#F8FAFC',
  bgSection: '#F1F5F9',
  textDark: '#1E293B',
  textMedium: '#475569',
  textLight: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  accent: PLANIVIO.teal,
  accentGreen: PLANIVIO.green
};

// Styles
const sectionStyle = {
  marginBottom: '40px',
  paddingBottom: '30px',
  borderBottom: `1px solid ${COLORS.border}`
};

const headingStyle = {
  fontSize: '1.4rem',
  fontWeight: 700,
  marginBottom: '20px',
  color: COLORS.textDark
};

const subHeadingStyle = {
  fontSize: '1.1rem',
  fontWeight: 600,
  marginTop: '25px',
  marginBottom: '12px',
  color: COLORS.textDark
};

const textStyle = {
  color: COLORS.textMedium,
  fontSize: '1rem',
  lineHeight: 1.8,
  marginBottom: '15px'
};

const listStyle = {
  color: COLORS.textMedium,
  lineHeight: 2,
  paddingLeft: '20px',
  marginBottom: '15px'
};

const linkStyle = {
  color: COLORS.accent,
  textDecoration: 'none'
};

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '12px 8px',
  color: COLORS.textDark,
  fontWeight: 600,
  borderBottom: `2px solid ${COLORS.border}`
};

const tableCellStyle = {
  padding: '12px 8px',
  color: COLORS.textMedium,
  borderBottom: `1px solid ${COLORS.border}`
};

function DatenschutzPage({ onNavigate }) {
  usePublicPageView('datenschutz');
  const { t, language, setLanguage, currentLanguage } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [showConsentToast, setShowConsentToast] = useState(false);

  // Lade aktuellen Consent-Status
  useEffect(() => {
    setAnalyticsEnabled(hasAnalyticsConsent());
  }, []);

  const handleToggleAnalytics = () => {
    if (analyticsEnabled) {
      // Widerrufen
      revokeAnalyticsConsent();
      setAnalyticsEnabled(false);
      setShowConsentToast(true);
      setTimeout(() => setShowConsentToast(false), 3000);
      console.log('🔒 Analytics deaktiviert');
    } else {
      // Aktivieren
      acceptAnalytics();
      setAnalyticsEnabled(true);
      setShowConsentToast(true);
      setTimeout(() => setShowConsentToast(false), 3000);
      console.log('✅ Analytics aktiviert');
    }
  };

  return (
    <div style={{ background: COLORS.bgWhite, minHeight: '100vh', color: COLORS.textDark }}>
      
      {/* Navigation */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        background: COLORS.bgWhite,
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderBottom: `1px solid ${COLORS.border}`,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
      }}>
        <div 
          onClick={() => onNavigate && onNavigate('landing')}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        >
          {/* ✅ v3: Echtes Planivio Logo */}
          <img 
            src="/images/Logos/planivio_logo_primary_400px.png" 
            alt="Planivio" 
            style={{ height: '36px', width: 'auto' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '2rem' }}>📋</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.accent }}>planivio</span>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Language Switcher Button */}
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            style={{
              padding: '8px 14px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.textMedium,
              cursor: 'pointer',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>{currentLanguage.flag}</span>
            <span>{currentLanguage.code.toUpperCase()}</span>
            <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>▼</span>
          </button>
          
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            style={{
              padding: '10px 24px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              color: COLORS.textMedium,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ← {t('common.back')}
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 40px' }}>
        
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontWeight: 800, 
          marginBottom: '20px',
          background: PLANIVIO.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {t('legal.privacy.title')}
        </h1>

        <p style={{ color: COLORS.textMuted, marginBottom: '40px', fontSize: '1rem' }}>
          {t('legal.privacy.lastUpdated')}: {t('legal.privacy.date')}
        </p>

        {/* Inhaltsverzeichnis */}
        <section style={{ ...sectionStyle, background: COLORS.bgLight, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
          <h2 style={headingStyle}>{t('legal.privacy.toc')}</h2>
          <ul style={{ color: COLORS.textMedium, lineHeight: 2, paddingLeft: '20px' }}>
            <li>1. {t('legal.privacy.sections.responsible')}</li>
            <li>2. {t('legal.privacy.sections.overview')}</li>
            <li>3. {t('legal.privacy.sections.legal')}</li>
            <li>4. {t('legal.privacy.sections.security')}</li>
            <li>5. {t('legal.privacy.sections.deletion')}</li>
            <li>6. {t('legal.privacy.sections.hosting')}</li>
            <li>7. {t('legal.privacy.sections.auth')}</li>
            <li>8. {t('legal.privacy.sections.contact')}</li>
            <li>9. {t('legal.privacy.sections.rights')}</li>
          </ul>
        </section>

        {/* 1. Verantwortlicher */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>1. {t('legal.privacy.sections.responsible')}</h2>
          <p style={textStyle}>
            <strong>Brice Fotzo</strong><br />
            Johannisstr. 105<br />
            90419 Nürnberg<br />
            {t('legal.privacy.germany')}
          </p>
          <p style={textStyle}>
            {t('legal.privacy.email')}: <a href="mailto:kontakt@planivio.de" style={linkStyle}>kontakt@planivio.de</a>
          </p>
          <p style={textStyle}>
            {t('legal.privacy.imprint')}:{' '}
            <span 
              onClick={() => onNavigate && onNavigate('impressum')}
              style={{ ...linkStyle, cursor: 'pointer' }}
            >
              {t('legal.privacy.imprintLink')}
            </span>
          </p>
        </section>

        {/* 2. Übersicht der Verarbeitungen */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>2. {t('legal.privacy.sections.overview')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.overviewText')}
          </p>
          
          <h3 style={subHeadingStyle}>{t('legal.privacy.dataTypes')}</h3>
          <ul style={listStyle}>
            <li>{t('legal.privacy.dataTypesList.inventory')}</li>
            <li>{t('legal.privacy.dataTypesList.content')}</li>
            <li>{t('legal.privacy.dataTypesList.usage')}</li>
            <li>{t('legal.privacy.dataTypesList.meta')}</li>
          </ul>

          <h3 style={subHeadingStyle}>{t('legal.privacy.affectedPersons')}</h3>
          <ul style={listStyle}>
            <li>{t('legal.privacy.affectedPersonsList.users')}</li>
          </ul>

          <h3 style={subHeadingStyle}>{t('legal.privacy.purposes')}</h3>
          <ul style={listStyle}>
            <li>{t('legal.privacy.purposesList.provision')}</li>
            <li>{t('legal.privacy.purposesList.contract')}</li>
            <li>{t('legal.privacy.purposesList.security')}</li>
            <li>{t('legal.privacy.purposesList.inquiries')}</li>
          </ul>
        </section>

        {/* 3. Rechtsgrundlagen */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>3. {t('legal.privacy.sections.legal')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.legalText')}
          </p>
          <ul style={listStyle}>
            <li>
              <strong>{t('legal.privacy.legalBasis.consent')}</strong> – {t('legal.privacy.legalBasis.consentDesc')}
            </li>
            <li>
              <strong>{t('legal.privacy.legalBasis.contract')}</strong> – {t('legal.privacy.legalBasis.contractDesc')}
            </li>
            <li>
              <strong>{t('legal.privacy.legalBasis.legitimate')}</strong> – {t('legal.privacy.legalBasis.legitimateDesc')}
            </li>
          </ul>
        </section>

        {/* 4. Sicherheitsmaßnahmen */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>4. {t('legal.privacy.sections.security')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.securityText')}
          </p>
          <p style={textStyle}>
            {t('legal.privacy.securityMeasures')}:
          </p>
          <ul style={listStyle}>
            <li>{t('legal.privacy.securityList.ssl')}</li>
            <li>{t('legal.privacy.securityList.passwords')}</li>
            <li>{t('legal.privacy.securityList.updates')}</li>
            <li>{t('legal.privacy.securityList.access')}</li>
            <li>{t('legal.privacy.securityList.rls')}</li>
          </ul>
        </section>

        {/* 5. Löschung von Daten */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>5. {t('legal.privacy.sections.deletion')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.deletionText1')}
          </p>
          <p style={textStyle}>
            {t('legal.privacy.deletionText2')}
          </p>
          <p style={textStyle}>
            {t('legal.privacy.deletionText3')}
          </p>
        </section>

        {/* 6. Webhosting */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>6. {t('legal.privacy.sections.hosting')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.hostingText')}
          </p>

          <h3 style={subHeadingStyle}>Microsoft Azure</h3>
          <p style={textStyle}>
            {t('legal.privacy.azureText')}
          </p>
          <p style={textStyle}>
            {t('legal.privacy.provider')}: Microsoft Corporation, One Microsoft Way, Redmond, WA 98052-6399, USA
          </p>
          <p style={textStyle}>
            {t('legal.privacy.privacyPolicy')}:{' '}
            <a 
              href="https://privacy.microsoft.com/de-de/privacystatement" 
              target="_blank" 
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://privacy.microsoft.com/de-de/privacystatement
            </a>
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.collectedData')}</h3>
          <ul style={listStyle}>
            <li>{t('legal.privacy.collectedDataList.ip')}</li>
            <li>{t('legal.privacy.collectedDataList.datetime')}</li>
            <li>{t('legal.privacy.collectedDataList.timezone')}</li>
            <li>{t('legal.privacy.collectedDataList.page')}</li>
            <li>{t('legal.privacy.collectedDataList.status')}</li>
            <li>{t('legal.privacy.collectedDataList.size')}</li>
            <li>{t('legal.privacy.collectedDataList.browser')}</li>
          </ul>
          <p style={textStyle}>
            <strong>{t('legal.privacy.legalBasisLabel')}:</strong> {t('legal.privacy.legitimateInterests')}
          </p>
        </section>

        {/* 7. Registrierung und Authentifizierung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>7. {t('legal.privacy.sections.auth')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.authText')}
          </p>

          <h3 style={subHeadingStyle}>Logto Cloud ({t('legal.privacy.authentication')})</h3>
          <p style={textStyle}>
            {t('legal.privacy.logtoText')}
          </p>
          <p style={textStyle}>
            {t('legal.privacy.provider')}: Silverhand Inc.
          </p>
          <p style={textStyle}>
            {t('legal.privacy.privacyPolicy')}:{' '}
            <a 
              href="https://logto.io/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://logto.io/privacy
            </a>
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.registrationData')}</h3>
          <ul style={listStyle}>
            <li>{t('legal.privacy.registrationDataList.email')}</li>
            <li>{t('legal.privacy.registrationDataList.username')}</li>
            <li>{t('legal.privacy.registrationDataList.password')}</li>
            <li>{t('legal.privacy.registrationDataList.timestamp')}</li>
          </ul>
          <p style={textStyle}>
            <strong>{t('legal.privacy.legalBasisLabel')}:</strong> {t('legal.privacy.contractFulfillment')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.accountDeletion')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.accountDeletionText')}
          </p>
        </section>

        {/* 8. Kontaktaufnahme */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>8. {t('legal.privacy.sections.contact')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.contactText')}
          </p>
          <ul style={listStyle}>
            <li><strong>{t('legal.privacy.processedData')}:</strong> {t('legal.privacy.contactDataList')}</li>
            <li><strong>{t('legal.privacy.legalBasisLabel')}:</strong> {t('legal.privacy.legitimateInterests')}</li>
            <li><strong>{t('legal.privacy.storageDuration')}:</strong> {t('legal.privacy.storageDurationText')}</li>
          </ul>
        </section>

        {/* 9. Cookies & Analytics */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>9. Cookies & Analytics</h2>

          <h3 style={subHeadingStyle}>9.1 Technisch notwendige Cookies</h3>
          <p style={textStyle}>
            Unsere Website verwendet technisch notwendige Cookies, um die Funktionalität
            und Sicherheit der Website zu gewährleisten. Diese Cookies erfordern keine
            Einwilligung gemäß Art. 6 Abs. 1 lit. f DSGVO.
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', background: COLORS.bgLight, borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Cookie-Name</th>
                <th style={tableHeaderStyle}>Zweck</th>
                <th style={tableHeaderStyle}>Dauer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tableCellStyle}>logto_*</td>
                <td style={tableCellStyle}>Authentifizierung</td>
                <td style={tableCellStyle}>Session</td>
              </tr>
              <tr>
                <td style={tableCellStyle}>planivio_cookie_consent</td>
                <td style={tableCellStyle}>Speichert Cookie-Präferenzen</td>
                <td style={tableCellStyle}>1 Jahr</td>
              </tr>
            </tbody>
          </table>

          <h3 style={subHeadingStyle}>9.2 Analytics-Cookies (nur mit Einwilligung)</h3>
          <p style={textStyle}>
            Mit Ihrer Einwilligung verwenden wir Analytics-Cookies, um die Nutzung unserer
            Website zu analysieren und die Benutzererfahrung zu verbessern.
          </p>

          <h4 style={{ ...subHeadingStyle, fontSize: '1rem', marginTop: '15px' }}>Welche Daten werden erhoben?</h4>
          <ul style={listStyle}>
            <li>Seitenaufrufe (welche Seiten werden besucht)</li>
            <li>Button-Klicks und Feature-Nutzung (z.B. "Rezept erstellt", "Budget geöffnet")</li>
            <li>Fehler und technische Probleme (Error-Logging)</li>
            <li>Session-ID (anonyme Sitzungs-Kennung)</li>
            <li>UTM-Parameter (Marketing-Kampagnen-Tracking)</li>
          </ul>

          <h4 style={{ ...subHeadingStyle, fontSize: '1rem', marginTop: '15px' }}>Zweck der Datenverarbeitung:</h4>
          <ul style={listStyle}>
            <li>Verbesserung der Benutzerfreundlichkeit</li>
            <li>Fehleranalyse und technische Optimierung</li>
            <li>Verständnis, welche Features genutzt werden</li>
            <li>Erfolgsmessung von Marketing-Kampagnen</li>
          </ul>

          <h4 style={{ ...subHeadingStyle, fontSize: '1rem', marginTop: '15px' }}>Rechtsgrundlage:</h4>
          <p style={textStyle}>
            Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) - Sie können Ihre Einwilligung jederzeit
            in den Datenschutz-Einstellungen widerrufen.
          </p>

          <h4 style={{ ...subHeadingStyle, fontSize: '1rem', marginTop: '15px' }}>Speicherdauer:</h4>
          <p style={textStyle}>
            Analytics-Daten werden maximal 12 Monate gespeichert und dann automatisch gelöscht.
          </p>

          {/* ✅ Analytics Opt-Out Panel */}
          <div style={{
            marginTop: '25px',
            padding: '20px',
            background: analyticsEnabled ? 'rgba(74, 144, 164, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            borderRadius: '12px',
            border: `2px solid ${analyticsEnabled ? COLORS.accent : '#ef4444'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px', color: COLORS.textDark }}>
                  {analyticsEnabled ? '✅ Analytics aktiviert' : '🔒 Analytics deaktiviert'}
                </h4>
                <p style={{ fontSize: '0.9rem', color: COLORS.textMedium, margin: 0 }}>
                  {analyticsEnabled
                    ? 'Sie unterstützen uns bei der Verbesserung von Planivio. Vielen Dank!'
                    : 'Analytics ist derzeit deaktiviert. Es werden keine Nutzungsdaten erfasst.'}
                </p>
              </div>
              <button
                onClick={handleToggleAnalytics}
                style={{
                  padding: '12px 24px',
                  background: analyticsEnabled ? '#ef4444' : PLANIVIO.gradient,
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: analyticsEnabled ? '0 2px 10px rgba(239, 68, 68, 0.3)' : `0 2px 10px ${PLANIVIO.shadow}`,
                  marginLeft: '20px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                }}
              >
                {analyticsEnabled ? 'Deaktivieren' : 'Aktivieren'}
              </button>
            </div>
          </div>

          {/* Toast Notification */}
          {showConsentToast && (
            <div style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              background: analyticsEnabled ? COLORS.accent : '#ef4444',
              color: 'white',
              padding: '15px 25px',
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              zIndex: 10000,
              animation: 'slideUp 0.3s ease-out'
            }}>
              {analyticsEnabled ? '✅ Analytics aktiviert' : '🔒 Analytics deaktiviert'}
            </div>
          )}

          <h3 style={subHeadingStyle}>9.3 Nicht verwendete Cookies/Tracking</h3>
          <ul style={listStyle}>
            <li>Keine Tracking-Cookies von Drittanbietern (Google Analytics, Facebook Pixel, etc.)</li>
            <li>Keine Marketing-Cookies von Drittanbietern</li>
            <li>Keine Social-Media-Tracking-Cookies</li>
            <li>Keine Werbe-Cookies</li>
          </ul>

          <p style={textStyle}>
            <strong>Rechtsgrundlage technische Cookies:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)
          </p>
        </section>

        {/* 10. Ihre Rechte */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>10. {t('legal.privacy.sections.rights')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.rightsText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.access')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.accessText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.rectification')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.rectificationText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.erasure')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.erasureText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.restriction')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.restrictionText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.portability')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.portabilityText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.objection')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.objectionText')}
          </p>

          <h3 style={subHeadingStyle}>{t('legal.privacy.rights.complaint')}</h3>
          <p style={textStyle}>
            {t('legal.privacy.rights.complaintText')}
          </p>
          <p style={textStyle}>
            Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
            Promenade 18<br />
            91522 Ansbach<br />
            <a 
              href="https://www.lda.bayern.de" 
              target="_blank" 
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://www.lda.bayern.de
            </a>
          </p>
        </section>

        {/* 11. Änderungen */}
        <section style={{ ...sectionStyle, borderBottom: 'none' }}>
          <h2 style={headingStyle}>11. {t('legal.privacy.changes.title')}</h2>
          <p style={textStyle}>
            {t('legal.privacy.changes.text')}
          </p>
          <p style={{ color: COLORS.textMuted, fontSize: '0.9rem', marginTop: '30px' }}>
            {t('legal.privacy.lastUpdated')}: {t('legal.privacy.date')}
          </p>
        </section>

      </div>

      {/* Footer */}
      <footer style={{
        padding: '30px 40px',
        borderTop: `1px solid ${COLORS.border}`,
        background: COLORS.bgLight,
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', gap: '25px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: COLORS.accent, fontSize: '0.9rem', fontWeight: 600 }}>
            {t('landing.footer.privacy')}
          </span>
          <span 
            onClick={() => onNavigate && onNavigate('agb')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.terms')}
          </span>
          <span 
            onClick={() => onNavigate && onNavigate('impressum')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.imprint')}
          </span>
        </div>
        <div style={{ marginTop: '20px', color: COLORS.textMuted, fontSize: '0.8rem' }}>
          © 2026 Planivio. {t('landing.footer.rights')} 🇩🇪
        </div>
      </footer>

      {/* Language Dropdown */}
      {showLanguageMenu && (
        <>
          <div 
            onClick={() => setShowLanguageMenu(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99998
            }}
          />
          <div style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            background: COLORS.bgWhite,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '8px',
            minWidth: '160px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 99999
          }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setShowLanguageMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: language === lang.code 
                    ? PLANIVIO.gradient 
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: language === lang.code ? 'white' : COLORS.textDark,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (language !== lang.code) {
                    e.target.style.background = COLORS.bgLight;
                  }
                }}
                onMouseLeave={(e) => {
                  if (language !== lang.code) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {language === lang.code && (
                  <span style={{ marginLeft: 'auto', opacity: 0.8 }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default DatenschutzPage;